<script setup>
import { ref, watch, computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
  stageCounts: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['back', 'save', 'delete-pipeline']);

const name = ref(props.pipeline.name || '');
const description = ref(props.pipeline.description || '');
const stages = ref(
  props.pipeline.stages ? JSON.parse(JSON.stringify(props.pipeline.stages)) : []
);

watch(
  () => props.pipeline,
  newPipeline => {
    if (newPipeline) {
      name.value = newPipeline.name || '';
      description.value = newPipeline.description || '';
      stages.value = newPipeline.stages
        ? JSON.parse(JSON.stringify(newPipeline.stages))
        : [];
    }
  },
  { immediate: true, deep: true }
);

const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#EAB308', // Yellow
  '#A855F7', // Purple
  '#6366F1', // Indigo
  '#22C55E', // Green
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#64748B', // Slate
];

function addStage() {
  const timestamp = Date.now();
  const nextColor = COLOR_PRESETS[stages.value.length % COLOR_PRESETS.length];
  stages.value.push({
    id: `stage_${timestamp}`,
    name: 'Nova Etapa',
    color: nextColor,
    label: `etapa_${timestamp}`,
  });
}

function removeStage(index) {
  stages.value.splice(index, 1);
}

function cycleColor(stage) {
  const currentIdx = COLOR_PRESETS.indexOf(stage.color);
  const nextIdx = (currentIdx + 1) % COLOR_PRESETS.length;
  stage.color = COLOR_PRESETS[nextIdx];
}

// Drag and drop reordering
const draggedIndex = ref(null);

function onDragStart(index) {
  draggedIndex.value = index;
}

function onDragOver(event, index) {
  event.preventDefault();
  if (draggedIndex.value === null || draggedIndex.value === index) return;

  const itemToMove = stages.value[draggedIndex.value];
  stages.value.splice(draggedIndex.value, 1);
  stages.value.splice(index, 0, itemToMove);
  draggedIndex.value = index;
}

function onDragEnd() {
  draggedIndex.value = null;
}

function handleSave() {
  if (!name.value.trim()) return;
  emit('save', {
    ...props.pipeline,
    name: name.value.trim(),
    description: description.value.trim(),
    stages: stages.value.map((s, idx) => ({
      ...s,
      name: s.name.trim() || `Etapa ${idx + 1}`,
      label: s.label || s.id,
    })),
  });
}
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-surface-1 overflow-y-auto">
    <!-- Top Navigation Header -->
    <header
      class="sticky top-0 z-20 flex h-16 w-full items-center justify-between border-b border-n-weak bg-n-surface-1/90 backdrop-blur-md px-6 shadow-sm"
    >
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="flex size-8 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors"
          title="Voltar ao Quadro"
          @click="emit('back')"
        >
          <Icon icon="i-lucide-arrow-left" class="size-4" />
        </button>

        <div class="flex items-center gap-2">
          <h1 class="text-lg font-extrabold text-n-slate-12 tracking-tight">
            {{ name || 'Configurações do Funil' }}
          </h1>
          <span
            class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-2 px-1.5 text-xs font-bold text-n-slate-11 border border-n-weak"
          >
            {{ stages.length }}
          </span>
          <span
            class="rounded-md bg-n-solid-3 px-2 py-0.5 text-[11px] font-mono text-n-slate-10 border border-n-weak"
          >
            ID: {{ pipeline.id }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-500/20 transition-all cursor-pointer"
          @click="emit('delete-pipeline', pipeline.id)"
        >
          <Icon icon="i-lucide-trash-2" class="size-3.5" />
          <span>Excluir Funil</span>
        </button>

        <button
          type="button"
          class="flex items-center gap-1.5 rounded-xl bg-n-brand px-5 py-2 text-xs font-extrabold text-[#1c1c1c] shadow hover:brightness-95 transition-all cursor-pointer"
          @click="handleSave"
        >
          <Icon icon="i-lucide-check" class="size-4" />
          <span>Salvar alterações</span>
        </button>
      </div>
    </header>

    <!-- Settings Content Body -->
    <main class="max-w-4xl w-full mx-auto p-6 space-y-8">
      <!-- Section 1: Informações Básicas -->
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1/40 p-6 shadow-xs"
      >
        <h2 class="text-base font-extrabold text-n-slate-12 mb-4">
          Informações básicas
        </h2>

        <div class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-n-slate-12 mb-1.5">
              Nome do funil
            </label>
            <input
              v-model="name"
              type="text"
              required
              class="reset-base !mb-0 w-full h-10 rounded-xl border border-n-weak bg-n-solid-1 px-3 text-xs text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-n-slate-12 mb-1.5">
              Descrição
            </label>
            <div class="relative">
              <textarea
                v-model="description"
                rows="4"
                maxlength="2000"
                placeholder="Acompanhe seus leads desde o contato inicial..."
                class="w-full rounded-xl border border-n-weak bg-n-solid-1 p-3 text-xs text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none resize-none"
              />
              <span
                class="absolute bottom-2.5 right-3 text-[11px] font-medium text-n-slate-10"
              >
                {{ description.length }} / 2000
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: Etapas -->
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1/40 p-6 shadow-xs"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <h2 class="text-base font-extrabold text-n-slate-12">Etapas</h2>
            <span
              class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-3 px-1.5 text-xs font-bold text-n-slate-11"
            >
              {{ stages.length }}
            </span>
          </div>
        </div>

        <!-- Reorderable Stage List -->
        <div class="space-y-2 mb-4">
          <div
            v-for="(stage, index) in stages"
            :key="stage.id || index"
            draggable="true"
            class="flex items-center gap-3 p-2.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-2xs hover:border-n-slate-6 transition-all group"
            @dragstart="onDragStart(index)"
            @dragover="onDragOver($event, index)"
            @dragend="onDragEnd"
          >
            <!-- Drag Handle -->
            <div class="cursor-grab text-n-slate-10 hover:text-n-slate-12 p-1">
              <Icon icon="i-lucide-grip-vertical" class="size-4" />
            </div>

            <!-- Color Picker Dot -->
            <button
              type="button"
              class="size-4 rounded-full flex-shrink-0 border border-black/10 cursor-pointer shadow-2xs transition-transform hover:scale-125"
              :style="{ backgroundColor: stage.color || '#3B82F6' }"
              title="Clique para alternar a cor da etapa"
              @click="cycleColor(stage)"
            />

            <!-- Stage Name Input -->
            <input
              v-model="stage.name"
              type="text"
              class="reset-base !mb-0 flex-1 h-8 rounded-lg border border-transparent hover:border-n-weak focus:border-n-brand bg-transparent px-2.5 text-xs font-extrabold text-n-slate-12 focus:outline-none"
            />

            <!-- Count Badge -->
            <span
              class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-2 px-1.5 text-xs font-bold text-n-slate-10 border border-n-weak"
              title="Negócios nesta etapa"
            >
              {{ stageCounts[stage.id] || 0 }}
            </span>

            <!-- Delete Stage -->
            <button
              type="button"
              class="p-1 text-n-slate-10 hover:text-red-500 transition-colors cursor-pointer"
              title="Excluir Etapa"
              @click="removeStage(index)"
            >
              <Icon icon="i-lucide-trash-2" class="size-4" />
            </button>
          </div>

          <div
            v-if="stages.length === 0"
            class="p-6 text-center text-xs text-n-slate-10 rounded-xl border border-dashed border-n-weak"
          >
            Nenhuma etapa cadastrada neste funil.
          </div>
        </div>

        <!-- Add Stage Button -->
        <button
          type="button"
          class="flex items-center gap-1.5 text-xs font-bold text-n-brand hover:underline cursor-pointer"
          @click="addStage"
        >
          <Icon icon="i-lucide-plus" class="size-4" />
          <span>Adicionar etapa</span>
        </button>
      </section>
    </main>
  </div>
</template>
